import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Loader2, Pencil } from "lucide-react";
import {
  useAnchoringDocuments,
  type AnchoredDocumentRow,
} from "~/api/v2/useAnchoringDocuments";
import { useBulkSelection } from "~/hooks/useBulkSelection";
import { Button } from "~/atoms/button";
import { CenterSpinner } from "~/atoms/spinner";
import ErrorState from "~/molecules/ErrorState";
import { AnchoringTable, type AnchoringColumn } from "./AnchoringTable";
import InviteQuizTakersModal from "./InviteQuizTakersModal";
import {
  CountsCell,
  CreatedByCell,
  DateCell,
  TagsCell,
  TitleLinkCell,
  UserCell,
} from "./cells";

export const AnchoredDocumentsTab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { anchored, isLoading, error, mutate } = useAnchoringDocuments();
  const selection = useBulkSelection(
    anchored.filter((row) => row.status === "ready").map((row) => row.quizId),
  );
  const [isInviteOpen, setInviteOpen] = useState(false);

  if (isLoading) return <CenterSpinner />;
  if (error) return <ErrorState />;

  const selectedQuizzes = anchored
    .filter((row) => selection.selectedIds.has(row.quizId))
    .map((row) => ({ quizId: row.quizId, category: row.category }));

  const columns: AnchoringColumn<AnchoredDocumentRow>[] = [
    {
      key: "edit",
      header: "",
      cell: (row) =>
        row.status === "generating" ? (
          <span className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" />
            {t(($) => $.anchoring.manage.creating)}
          </span>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={(event) => {
              event.stopPropagation();
              void navigate(`/anchoring/${row.quizId}?mode=edit`);
            }}
          >
            <Pencil className="size-4" />
            {t(($) => $.anchoring.manage.edit)}
          </Button>
        ),
    },
    {
      key: "title",
      header: t(($) => $.anchoring.columns.title),
      cell: (row) => (
        <TitleLinkCell
          documentId={row.documentId}
          documentVersionId={row.documentVersionId}
          title={row.title}
          tab="quiz"
        />
      ),
    },
    {
      key: "takers",
      header: t(($) => $.anchoring.columns.takers),
      cell: (row) =>
        row.status === "generating" ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <CountsCell
            invitedCount={row.invitedCount}
            completedCount={row.completedCount}
          />
        ),
    },
    {
      key: "createdBy",
      header: t(($) => $.anchoring.columns.createdBy),
      cell: (row) => <CreatedByCell createdBy={row.createdBy} />,
    },
    {
      key: "createdAt",
      header: t(($) => $.anchoring.columns.createdAt),
      cell: (row) => <DateCell value={row.createdAt} />,
    },
    {
      key: "owner",
      header: t(($) => $.anchoring.columns.owner),
      cell: (row) => <UserCell user={row.owner} />,
    },
    {
      key: "publisher",
      header: t(($) => $.anchoring.columns.publisher),
      cell: (row) => <UserCell user={row.publisher} />,
    },
    {
      key: "publishedAt",
      header: t(($) => $.anchoring.columns.publishedAt),
      cell: (row) => <DateCell value={row.publishedAt} />,
    },
    {
      key: "adoptedAt",
      header: t(($) => $.anchoring.columns.adoptedAt),
      cell: (row) => <DateCell value={row.adoptedAt} />,
    },
    {
      key: "tags",
      header: t(($) => $.anchoring.columns.tags),
      cell: (row) => <TagsCell tags={row.tags} />,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button
          variant="outline"
          disabled={selection.selectedIds.size === 0}
          onClick={() => setInviteOpen(true)}
        >
          {t(($) => $.anchoring.actions.inviteTakers)}
        </Button>
      </div>
      <AnchoringTable
        columns={columns}
        rows={anchored}
        getRowId={(row) => row.quizId}
        emptyMessage={t(($) => $.anchoring.empty.anchored)}
        selection={selection}
        isRowSelectable={(row) => row.status === "ready"}
        onRowClick={(row) => {
          if (row.status === "generating") return;
          void navigate(`/anchoring/${row.quizId}`);
        }}
      />
      <InviteQuizTakersModal
        quizzes={selectedQuizzes}
        open={isInviteOpen}
        onOpenChange={setInviteOpen}
        onInvited={() => {
          selection.clearSelection();
          void mutate();
        }}
      />
    </div>
  );
};
